'use client';

import { createContext, useCallback, useContext, useMemo } from 'react';
import { keepPreviousData } from '@tanstack/query-core';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsIsoDateTime,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from 'nuqs';

import { DateRangeValue } from '@/components/daterange';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';

export type Duration = 'all' | 'lt_30' | '30_120' | 'gt_120';
export type Sort = 'newest' | 'oldest';
export type SortScore = 'better_first' | 'better_last' | 'none';

export const BOOKING_STATUSES_OPTIONS = [
  { label: 'Any booking status', value: 'all' },
  { label: 'Eligible (not-booked)', value: 'eligible_not_booked' },
  { label: 'Eligible (booked)', value: 'eligible_booked' },
  { label: 'Excluded', value: 'excluded' },
] as const;
export type BookingStatus = (typeof BOOKING_STATUSES_OPTIONS)[number]['value'];

type CallData = ApiSchemas['ExternalCallListOut'];

export interface Filters {
  dateRange: DateRangeValue | null;
  duration?: Duration;
  csr?: string[];
  team?: string[];
  search?: string;
  booking_status?: BookingStatus;
}

export interface SearchState {
  sortBy: Sort;
  sortScore: SortScore;
  filters: Filters;
  page: number;
  pageSize: number;
}

export interface CallsTableContextValue {
  searchState: SearchState;
  setSortBy: (sortBy: Sort) => void;
  setSortScore: (sortScore: SortScore) => void;
  setFilters: (filters: Partial<Filters>) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  calls: CallData[];
  isLoading: boolean;
  totalCount: number;
}

const CallsTableCtx = createContext<CallsTableContextValue | null>(null);

export function useCallsTableCtx() {
  const ctx = useContext(CallsTableCtx);
  if (!ctx) {
    throw new Error('useCallsTableCtx must be used within a CallsTableProvider');
  }
  return ctx;
}

export function CallsTableProvider({ children }: { children: React.ReactNode }) {
  const { api } = useApiV1Ctx();
  const { officeId } = useOfficeCtx();

  // Use nuqs to manage URL state for all filters
  const [searchState, setSearchState] = useQueryStates(
    {
      sortBy: parseAsStringEnum(['newest', 'oldest']).withDefault('newest'),
      page: parseAsInteger.withDefault(1),
      pageSize: parseAsInteger.withDefault(25),
      // Date range
      from: parseAsIsoDateTime,
      to: parseAsIsoDateTime,
      sortScore: parseAsStringEnum(['better_first', 'better_last', 'none']).withDefault('none'),
      // Filters
      duration: parseAsStringEnum(['all', 'lt_30', '30_120', 'gt_120']).withDefault('all'),
      csr: parseAsArrayOf(parseAsString, ',').withDefault([]),
      team: parseAsArrayOf(parseAsString, ',').withDefault([]),
      search: parseAsString,
      booking_status: parseAsStringEnum([...BOOKING_STATUSES_OPTIONS.map((o) => o.value)]).withDefault('all'),
    },
    {
      history: 'push',
      shallow: false,
    }
  );

  // Convert nuqs state to our expected format
  const filters = useMemo<Filters>(
    () => ({
      dateRange: searchState.from
        ? {
            from: searchState.from,
            to: searchState.to ?? undefined,
          }
        : null,
      duration: searchState.duration,
      csr: searchState.csr,
      team: searchState.team,
      search: searchState.search || undefined,
      booking_status: searchState.booking_status,
    }),
    [searchState]
  );

  const formattedSearchState = useMemo<SearchState>(
    () => ({
      sortBy: searchState.sortBy,
      sortScore: searchState.sortScore,
      filters,
      page: searchState.page,
      pageSize: searchState.pageSize,
    }),
    [searchState.sortBy, searchState.sortScore, filters, searchState.page, searchState.pageSize]
  );

  const durationFilter = () => {
    switch (formattedSearchState.filters.duration) {
      case 'lt_30':
        return {
          duration_ms__gte: 0,
          duration_ms__lte: 30000,
        };
      case '30_120':
        return {
          duration_ms__gte: 30000,
          duration_ms__lte: 120000,
        };
      case 'gt_120':
        return { duration_ms__gte: 120000 };
      case 'all':
        return {};
    }
  };

  const orderFilter = () => {
    const order: { order_by?: string; scores__order_by?: string } = {};
    switch (formattedSearchState.sortBy) {
      case 'newest':
        order.order_by = '-start_time';
        break;
      case 'oldest':
        order.order_by = 'start_time';
        break;
    }

    if (formattedSearchState.sortScore !== 'none') {
      switch (formattedSearchState.sortScore) {
        case 'better_first':
          order.scores__order_by = '-metrics_avg_score';
          break;
        case 'better_last':
          order.scores__order_by = '+metrics_avg_score';
          break;
      }
    }

    return order;
  };

  const searchFilter = () => {
    if (!formattedSearchState.filters.search) return {};
    return {
      search: formattedSearchState.filters.search,
    };
  };

  const csrFilter = () => {
    if (!formattedSearchState.filters.csr || formattedSearchState.filters.csr.length === 0) return {};
    return {
      user_name__in: formattedSearchState.filters.csr.join(','),
    };
  };

  const teamFilter = () => {
    if (!formattedSearchState.filters.team || formattedSearchState.filters.team.length === 0) return {};
    return {
      team__in: formattedSearchState.filters.team.join(','),
    };
  };

  const bookingStatusFilter =
    !formattedSearchState.filters.booking_status || formattedSearchState.filters.booking_status === 'all'
      ? undefined
      : formattedSearchState.filters.booking_status;

  const { data: response, isLoading } = api.externalCalls.searchExternalCallsGet.useQuery(
    {
      query: {
        office_id: officeId,
        page: formattedSearchState.page,
        page_size: formattedSearchState.pageSize,
        is_duration_below_threshold: false,
        start_time__gte: formattedSearchState.filters.dateRange?.from?.toISOString(),
        end_time__lte: formattedSearchState.filters.dateRange?.to?.toISOString(),
        scores__booking_status: bookingStatusFilter,
        ...durationFilter(),
        ...orderFilter(),
        ...searchFilter(),
        ...csrFilter(),
        ...teamFilter(),
      },
    },
    {
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    }
  );

  const setSortBy = useCallback(
    (sortBy: Sort) => {
      setSearchState({ sortBy, page: 1 });
    },
    [setSearchState]
  );

  const setSortScore = useCallback(
    (sortScore: SortScore) => {
      setSearchState({ sortScore, page: 1 });
    },
    [setSearchState]
  );
  const setFilters = useCallback(
    (filters: Partial<Filters>) => {
      const updates: Partial<typeof searchState> = { page: 1 };

      if (filters.dateRange) {
        updates.from = filters.dateRange.from;
        updates.to = filters.dateRange.to;
      } else {
        updates.from = null;
        updates.to = null;
      }

      if (filters.duration !== undefined) {
        updates.duration = filters.duration;
      }
      if (filters.csr !== undefined) {
        updates.csr = filters.csr;
      }
      if (filters.team !== undefined) {
        updates.team = filters.team;
      }
      if (filters.search !== undefined) {
        updates.search = filters.search || null;
      }
      if (filters.booking_status !== undefined) {
        updates.booking_status = filters.booking_status;
      }

      setSearchState(updates);
    },
    [setSearchState]
  );

  const setPage = useCallback(
    (page: number) => {
      setSearchState({ page });
    },
    [setSearchState]
  );

  const setPageSize = useCallback(
    (pageSize: number) => {
      setSearchState({ pageSize, page: 1 });
    },
    [setSearchState]
  );

  const value = useMemo<CallsTableContextValue>(() => {
    return {
      searchState: formattedSearchState,
      setSortBy,
      setSortScore,
      setFilters,
      setPage,
      setPageSize,
      calls: response?.data ?? [],
      isLoading,
      totalCount: response?.total ?? 0,
    };
  }, [
    formattedSearchState,
    setSortBy,
    setSortScore,
    setFilters,
    setPage,
    setPageSize,
    response?.data,
    isLoading,
    response?.total,
  ]);
  return <CallsTableCtx.Provider value={value}>{children}</CallsTableCtx.Provider>;
}
